import React, { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Sparkles,
  CheckCircle2,
  UserPlus,
  UserCheck,
  Gift,
  Copy,
  ExternalLink,
} from "lucide-react";
import styles from "./FeedSuggestionsPanel.module.css";
import { DUMMY_CREATORS, TRENDING_VAULT_ITEMS, Creator } from "./dummyFeedData";
import { showToast } from "../../../utils/toast";

export default function FeedSuggestionsPanel() {
  const history = useHistory();
  const [creators, setCreators] = useState<Creator[]>(DUMMY_CREATORS);

  const toggleFollow = (id: string, name: string) => {
    setCreators((prev) =>
      prev.map((c) => {
        if (c.id === id) {
          const newState = !c.isFollowing;
          showToast(newState ? `Followed ${name}!` : `Unfollowed ${name}`);
          return { ...c, isFollowing: newState };
        }
        return c;
      })
    );
  };

  const copyReferral = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(`${window.location.origin}/register?ref=VIPMEMBER`);
      showToast("Referral link copied to clipboard!");
    } else {
      showToast("Referral link: sugarcribs.com/register?ref=VIPMEMBER");
    }
  };

  return (
    <aside className={styles.panel}>
      {/* Suggested Creators Card */}
      <div className={styles.widgetCard}>
        <div className={styles.widgetHeader}>
          <h3 className={styles.widgetTitle}>Suggested Creators</h3>
          <button
            className={styles.seeAllLink}
            onClick={() => history.push("/creators")}
          >
            See all
          </button>
        </div>

        <div className={styles.creatorsList}>
          {creators.slice(0, 4).map((creator) => (
            <div key={creator.id} className={styles.creatorRow}>
              <div
                className={styles.creatorInfo}
                onClick={() => history.push(`/${creator.username}`)}
              >
                <img
                  src={creator.avatar}
                  alt={creator.name}
                  className={styles.creatorAvatar}
                />
                <div className={styles.creatorMeta}>
                  <div className={styles.creatorNameRow}>
                    <span className={styles.creatorName}>{creator.name}</span>
                    {creator.isVerified && (
                      <CheckCircle2 size={13} color="#fe5a97" />
                    )}
                  </div>
                  <span className={styles.creatorCategory}>
                    {creator.category}
                  </span>
                </div>
              </div>

              <button
                className={`${styles.followBtn} ${
                  creator.isFollowing ? styles.followBtnActive : ""
                }`}
                onClick={() => toggleFollow(creator.id, creator.name)}
              >
                {creator.isFollowing ? (
                  <>
                    <UserCheck size={13} />
                    <span>Following</span>
                  </>
                ) : (
                  <>
                    <UserPlus size={13} />
                    <span>Follow</span>
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Trending Vault Drops Card */}
      <div className={styles.widgetCard}>
        <div className={styles.widgetHeader}>
          <h3 className={styles.widgetTitle}>
            <Sparkles size={16} color="#ffd700" />
            <span>The Vault Drops</span>
          </h3>
          <button
            className={styles.seeAllLink}
            onClick={() => history.push("/the-vault")}
          >
            Explore Vault
          </button>
        </div>

        <div className={styles.vaultDropsList}>
          {TRENDING_VAULT_ITEMS.map((drop) => (
            <div
              key={drop.id}
              className={styles.vaultDropCard}
              onClick={() => history.push("/the-vault")}
            >
              <img
                src={drop.coverImage}
                alt={drop.title}
                className={styles.vaultDropImg}
              />
              <div className={styles.vaultDropOverlay}>
                <h4 className={styles.vaultDropTitle}>{drop.title}</h4>
                <div className={styles.vaultDropMeta}>
                  <span>By {drop.creator}</span>
                  <span className={styles.vaultPriceBadge}>{drop.price}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* VIP Referral Card */}
      <div className={styles.referralCard}>
        <div className={styles.widgetHeader}>
          <h4 className={styles.referralTitle}>Invite & Earn Credits</h4>
          <Gift size={18} color="#fe5a97" />
        </div>
        <p className={styles.referralDesc}>
          Get 50 bonus credits for every friend who joins SugarCribs with your VIP link.
        </p>
        <button className={styles.referralBtn} onClick={copyReferral}>
          <Copy size={14} />
          <span>Copy Invite Link</span>
        </button>
      </div>

      {/* Mini Footer */}
      <div className={styles.footerLinks}>
        <Link to="/terms-of-service" className={styles.footerLink}>Terms</Link>
        <Link to="/privacy-policy" className={styles.footerLink}>Privacy</Link>
        <Link to="/help" className={styles.footerLink}>Help Center</Link>
        <Link to="/acceptable-use-policy" className={styles.footerLink}>Guidelines</Link>
        <Link to="/genesis-collection" className={styles.footerLink}>Genesis</Link>
      </div>
      <p className={styles.copyright}>© 2026 SugarCribs VIP. All rights reserved.</p>
    </aside>
  );
}
